import * as React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Calendar } from "lucide-react";
import { Button } from "../../shadcn/components/ui/Button";
import { Input } from "../../shadcn/components/ui/Input";
import { Label } from "../../shadcn/components/ui/Label";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import "react-datepicker/dist/react-datepicker.css";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../shadcn/components/ui/Select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../shadcn/components/ui/Popover";
import { Calendar as CalendarComponent } from "../../shadcn/components/ui/Calendar";
import { format } from "date-fns";
import { WelcomeAnimation } from "./Welcome";
import DatePicker from "react-datepicker";
import { useContext } from "react";
import countries from "country-list";
import { FormContext } from "./FormContext";
import { getNames } from "country-list";

export function ThirdStep() {
  const navigate = useNavigate();
  const { formData, updateFormData } = useContext(FormContext);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [showWelcome, setShowWelcome] = React.useState(false);

  const countryList = countries.getNames();

  // Convert birthDate to a Date object for the DatePicker
  const selectedDate = formData.profileDetails.birthDate
    ? new Date(formData.profileDetails.birthDate)
    : null;

  const handleChange = (field, value) => {
    updateFormData({ [field]: value }); // Use updateFormData from context
  };

  const handleDateChange = (date) => {
    updateFormData({ birthDate: date.toISOString() }); // Convert to ISO string and update
  };

  const handleNext = async () => {
    const { education, countryOfBirth, birthDate } = formData.profileDetails;
    if (!education || !countryOfBirth || !birthDate) {
      setErrorMessage("Please fill out all fields before proceeding.");
      return;
    }

    // Save data to localStorage (optional)
    localStorage.setItem("education", education);
    localStorage.setItem("countryOfBirth", countryOfBirth);
    localStorage.setItem("birthDate", birthDate.toISOString());

    // Submit data to the API
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        "https://silver-tlc-backend-delta.vercel.app/api/v1/signupjourney/create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            token: token,
            profileDetails: formData.profileDetails,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log("Data saved successfully:", data);
        setShowWelcome(true);

        // Redirect after 3 seconds
        setTimeout(() => {
          const role = localStorage.getItem("signupRole"); // Role set during OTP verification
          const redirectPath = localStorage.getItem("redirectAfterLogin");
          localStorage.removeItem("signupRole"); // Clean up after use

          if (redirectPath) {
            // Redirect back to the property details page
            localStorage.removeItem("redirectAfterLogin");
            navigate(redirectPath);
          } else {
            switch (role) {
              case "Individual":
                navigate("/dashboard/My profile");
                break;
              case "Property Owner":
                navigate("/propertyownerdashboard");
                break;
              case "Managed Care Organizations":
                navigate("/Managed%20Care%20Organization's");
                break;
              case "Real Estate Professionals":
                navigate("/Hospital/HospitalSystems");
                break;
              case "Service Provider":
                navigate("/servicedashboard");
                break;
              case "Non Profits":
                navigate("/501%20C3%20Corporations");
                break;
              default:
                navigate("/");
                break;
            }
          }
        }, 3000); // Adjust the timeout duration based on your animation
      } else {
        console.error("Failed to save data");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleSkip = () => {
    // Redirect to the appropriate dashboard without saving form data
    const role = localStorage.getItem("signupRole");
    const redirectPath = localStorage.getItem("redirectAfterLogin");
    localStorage.removeItem("signupRole");

    if (redirectPath) {
      // Redirect back to the property details page
      localStorage.removeItem("redirectAfterLogin");
      navigate(redirectPath);
    } else {
      // Redirect to the respective dashboard based on role
      switch (role) {
        case "Individual":
          navigate("/dashboard/My profile");
          break;
        case "Property Owner":
          navigate("/propertyownerdashboard");
          break;
        case "Hospital System/Managed Care Organizations":
          navigate("/HospitalManagement");
          break;
        case "Real Estate Professionals":
          navigate("/realestatedashboard");
          break;
        case "Service Provider":
          navigate("/servicedashboard");
          break;
        case "Non Profits":
          navigate("/nonprofit");
          break;
        default:
          navigate("/"); // Fallback dashboard
          break;
      }
    }
  };

  if (showWelcome) {
    return <WelcomeAnimation />;
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row w-full">
      {/* Left Section */}
      <motion.div
        className="bg-purple-600 text-white lg:px-[10rem] md:px-[5rem] p-10 flex flex-col justify-center md:w-1/2"
        initial={{ x: -50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="mb-12">
          <motion.div
            className="flex items-center mb-8"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <img src={logo} alt="logo" />
            <span className="text-2xl font-bold">Silver TLC</span>
          </motion.div>
          <motion.h1
            className="text-4xl font-bold mb-4"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Last Step !!
          </motion.h1>
          <motion.p
            className="text-xl"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            We're excited to have you on board. Let's get started by collecting
            some basic information.
          </motion.p>
        </div>
      </motion.div>

      {/* Right Section */}
      <motion.div
        className="bg-white lg:px-[10rem] md:px-[5rem] p-10 flex flex-col justify-center"
        initial={{ x: 50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <form
          className="space-y-6 max-w-3xl mx-auto"
          onSubmit={(e) => e.preventDefault()}
        >
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="w-[25rem]"
          >
            <Label htmlFor="education">Education</Label>
            <Select onValueChange={(value) => handleChange("education", value)}>
              <SelectTrigger>
                <SelectValue placeholder="Education" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Doctoral">Doctoral Degree</SelectItem>
                <SelectItem value="Associate">Associate Degree</SelectItem>
                <SelectItem value="Bachelor">Bachelor's Degree</SelectItem>
                <SelectItem value="Masters">Masters</SelectItem>
                <SelectItem value="Graduate">Graduate</SelectItem>
                <SelectItem value="Professional">
                  Professional Degree
                </SelectItem>
              </SelectContent>
            </Select>
          </motion.div>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <Label htmlFor="countryOfBirth">Country of Birth</Label>
            <Select
              onValueChange={(value) => handleChange("countryOfBirth", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select your country of birth" />
              </SelectTrigger>
              <SelectContent>
                {countryList.map((country) => (
                  <SelectItem key={country} value={country}>
                    {country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <Label className="mr-2">Birth Date</Label>
            <DatePicker
              selected={selectedDate} // Pass the Date object
              onChange={handleDateChange} // Handle date selection
              dateFormat="PPP" // Display format
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              customInput={
                <Button
                  variant={"outline"}
                  className={`w-full justify-start text-left font-normal ${
                    !selectedDate ? "text-muted-foreground" : ""
                  }`}
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {selectedDate ? (
                    format(selectedDate, "PPP") // Format the selected date
                  ) : (
                    <span>Pick a date</span>
                  )}
                </Button>
              }
            />
          </motion.div>

          {errorMessage && (
            <motion.p
              className="text-red-500 text-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {errorMessage}
            </motion.p>
          )}

          <motion.div
            className="flex justify-between pt-4"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <Button variant="outline" onClick={handleSkip}>
              Skip
            </Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleNext}
            >
              Next
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        </form>
      </motion.div>
    </div>
  );
}
